import { Form, Input, Modal, Select, Switch, DatePicker } from "antd";
import { AppButton } from "components/button/AppButton";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useQueryClient } from "react-query";
import { IdentifierProps } from "types";
import { generalValidationRules } from "utils/formHelpers/validations";
import { openNotification } from "utils/notification";
import { useCreateCampaign } from "../hooks/useCreateCampaign";
import { QUERY_KEY_CAMPAIGNS } from "../hooks/useGetCampaigns";
import dayjs from "dayjs";

export const AddCampaign = ({ open, handleClose }: IdentifierProps) => {
  const [form] = Form.useForm();
  const { token } = useGetUserInfo();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useCreateCampaign();

  const handleSubmit = (values: any) => {
    mutate(
      {
        token,
        name: values.name,
        code: values.code || undefined,
        description: values.description,
        target_type: values.target_type,
        utm_source: values.utm_source,
        utm_medium: values.utm_medium,
        utm_campaign: values.utm_campaign,
        utm_term: values.utm_term,
        utm_content: values.utm_content,
        is_active: values.is_active ?? true,
        starts_at: values.starts_at
          ? dayjs(values.starts_at).toISOString()
          : null,
        expires_at: values.expires_at
          ? dayjs(values.expires_at).toISOString()
          : null,
      },
      {
        onError: (err: any) => {
          const res = err.response?.data;
          let description = "An error occurred, please try again.";
          if (res?.message && typeof res.message === "string") {
            description = res.message;
          }
          if (res?.errors && typeof res.errors === "object") {
            const first = Object.values(res.errors)[0];
            if (Array.isArray(first) && first[0]) {
              description = String(first[0]);
            }
          }
          openNotification({
            title: "Error",
            state: "error",
            description,
            duration: 6,
          });
        },
        onSuccess: () => {
          openNotification({
            title: "Success",
            state: "success",
            description: "Campaign created successfully",
            duration: 4.5,
          });
          form.resetFields();
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY_CAMPAIGNS] });
          handleClose();
        },
      }
    );
  };

  return (
    <Modal
      title="Create marketing campaign"
      open={open}
      onCancel={() => handleClose()}
      footer={null}
      width="42rem"
      style={{ top: 10 }}
      destroyOnClose
    >
      <Form
        layout="vertical"
        requiredMark={false}
        form={form}
        onFinish={handleSubmit}
        initialValues={{ target_type: "any", is_active: true }}
      >
        <Form.Item name="name" label="Campaign name" rules={generalValidationRules}>
          <Input placeholder="LinkedIn Q3 Recruiter Push" />
        </Form.Item>
        <Form.Item
          name="code"
          label="Tracking code (optional)"
          extra="Leave blank to auto-generate. Letters, numbers, hyphens only."
          rules={[
            {
              pattern: /^[a-zA-Z0-9\-_|]*$/,
              message: "Only letters, numbers, hyphens, underscores, pipes",
            },
          ]}
        >
          <Input placeholder="li-q3-recruiters" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} placeholder="Internal notes for marketing" />
        </Form.Item>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
          <Form.Item name="target_type" label="Target audience" rules={generalValidationRules}>
            <Select
              options={[
                { value: "any", label: "Any (default talent signup)" },
                { value: "talent", label: "Talent" },
                { value: "recruiter", label: "Recruiter" },
              ]}
            />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="starts_at" label="Starts at (optional)">
            <DatePicker showTime className="w-full" />
          </Form.Item>
          <Form.Item name="expires_at" label="Expires at (optional)">
            <DatePicker showTime className="w-full" />
          </Form.Item>
          <Form.Item name="utm_source" label="UTM source">
            <Input placeholder="linkedin" />
          </Form.Item>
          <Form.Item name="utm_medium" label="UTM medium">
            <Input placeholder="cpc" />
          </Form.Item>
          <Form.Item name="utm_campaign" label="UTM campaign">
            <Input placeholder="q3-recruiters" />
          </Form.Item>
          <Form.Item name="utm_content" label="UTM content">
            <Input placeholder="ad-variant-a" />
          </Form.Item>
        </div>
        <div className="flex justify-end mt-2">
          <AppButton label="Create campaign" type="submit" isLoading={isLoading} />
        </div>
      </Form>
    </Modal>
  );
};
